import { useEffect, useRef, useState } from "react";
import { App, Button, Flex, Input, Popconfirm, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/client";
import {
  addSecurityRule,
  deleteSecurityRule,
  replaceSecurityRules,
  updateSecurityRule,
  type SecurityRuleType,
} from "@/api/security";
import BrandButton from "@/components/BrandButton";
import type { SecurityRule } from "@/types";
import { BatchModifyModal } from "./BatchModifyModal";
import { SECURITY_MAX, isValidEntry, type SecurityKind } from "./rules";
import styles from "./security.module.scss";

const RULE_TYPE: Record<SecurityKind, SecurityRuleType> = {
  redirect: "redirect_url",
  ip: "ip_whitelist",
  h5: "h5_trusted_domain",
};

const COPY = {
  redirect: {
    title: "security.redirectTitle",
    desc: "security.redirectDesc",
    placeholder: "security.urlPlaceholder",
    col: "security.colUrl",
    invalid: "security.invalidUrl",
    duplicate: "security.duplicateUrl",
  },
  ip: {
    title: "security.ipTitle",
    desc: "security.ipDesc",
    placeholder: "security.ipPlaceholder",
    col: "security.colIp",
    invalid: "security.invalidIp",
    duplicate: "security.duplicateIp",
  },
  h5: {
    title: "security.h5Title",
    desc: "security.h5Desc",
    placeholder: "security.urlPlaceholder",
    col: "security.colDomain",
    invalid: "security.invalidUrl",
    duplicate: "security.duplicateDomain",
  },
} as const;

export function SecuritySection({
  kind,
  appId,
  list,
  onListChange,
}: {
  kind: SecurityKind;
  appId: string;
  list: SecurityRule[];
  onListChange: (updater: (list: SecurityRule[]) => SecurityRule[]) => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const copy = COPY[kind];

  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingIdRef = useRef<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [batchOpen, setBatchOpen] = useState(false);

  useEffect(() => {
    editingIdRef.current = null;
    setEditingId(null);
    setEditValue("");
    setEditError("");
    setValue("");
    setError("");
  }, [kind]);

  const atMax = list.length >= SECURITY_MAX;

  const submitAdd = () => {
    const trimmed = value.trim();
    if (addingRef.current) return;
    if (atMax) {
      setError(t("security.maxReached", { max: SECURITY_MAX }));
      return;
    }
    if (!isValidEntry(kind, trimmed)) {
      setError(t(copy.invalid));
      return;
    }
    if (list.some((item) => item.value === trimmed)) {
      setError(t(copy.duplicate));
      return;
    }
    addingRef.current = true;
    setAdding(true);
    void addSecurityRule(appId, { ruleType: RULE_TYPE[kind], value: trimmed })
      .then((created) => {
        if (created.value) {
          onListChange((current) =>
            current.some((item) => item.value === created.value)
              ? current
              : [...current, { id: created.id, value: created.value }],
          );
        }
        setValue("");
        setError("");
      })
      .catch((err: unknown) => {
        const text =
          err instanceof ApiError && err.message
            ? err.message
            : t("security.addFailed");
        message.error(text);
      })
      .finally(() => {
        addingRef.current = false;
        setAdding(false);
      });
  };

  const startEdit = (row: SecurityRule) => {
    if (!row.id) {
      message.error(t("security.saveFailed"));
      return;
    }
    editingIdRef.current = row.id;
    setEditingId(row.id);
    setEditValue(row.value);
    setEditError("");
  };

  const cancelEdit = () => {
    editingIdRef.current = null;
    setEditingId(null);
    setEditValue("");
    setEditError("");
  };

  const confirmDelete = (row: SecurityRule) => {
    if (!row.id) {
      message.error(t("security.deleteFailed"));
      return;
    }
    return deleteSecurityRule(appId, row.id)
      .then(() => {
        onListChange((current) => current.filter((item) => item.id !== row.id));
      })
      .catch((err: unknown) => {
        const text =
          err instanceof ApiError && err.message
            ? err.message
            : t("security.deleteFailed");
        message.error(text);
      });
  };

  const saveEdit = () => {
    if (!editingId || savingRef.current) return;
    const current = list.find((item) => item.id === editingId);
    if (!current) return;
    const trimmed = editValue.trim();
    if (!isValidEntry(kind, trimmed)) {
      setEditError(t(copy.invalid));
      return;
    }
    if (list.some((item) => item.id !== editingId && item.value === trimmed)) {
      setEditError(t(copy.duplicate));
      return;
    }
    if (trimmed === current.value) {
      cancelEdit();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const ruleId = editingId;
    void updateSecurityRule(appId, ruleId, trimmed)
      .then((updated) => {
        if (updated.value) {
          onListChange((current) =>
            current.map((item) =>
              item.id === ruleId ? { ...item, value: updated.value } : item,
            ),
          );
        }
        if (editingIdRef.current === ruleId) cancelEdit();
      })
      .catch((err: unknown) => {
        const text =
          err instanceof ApiError && err.message
            ? err.message
            : t("security.saveFailed");
        message.error(text);
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
      });
  };

  const columns: ColumnsType<SecurityRule> = [
    {
      title: t(copy.col),
      dataIndex: "value",
      render: (_, row) =>
        editingId === row.id ? (
          <div>
            <Input
              value={editValue}
              maxLength={512}
              autoFocus
              status={editError ? "error" : undefined}
              placeholder={t(copy.placeholder)}
              onChange={(e) => {
                setEditValue(e.target.value);
                setEditError("");
              }}
              onPressEnter={saveEdit}
            />
            {editError ? (
              <Typography.Text type="danger" className="!mt-1 block">
                {editError}
              </Typography.Text>
            ) : null}
          </div>
        ) : (
          row.value
        ),
    },
    {
      title: t("security.colActions"),
      key: "actions",
      width: 140,
      align: "right",
      render: (_, row) =>
        editingId === row.id ? (
          <Flex justify="flex-end" gap={16}>
            <Button
              type="link"
              className="!px-0 !text-accent"
              loading={saving}
              onClick={saveEdit}
            >
              {t("common.save")}
            </Button>
            <Button
              type="link"
              className="!px-0 !text-accent"
              onClick={cancelEdit}
            >
              {t("common.cancel")}
            </Button>
          </Flex>
        ) : (
          <Flex justify="flex-end" gap={16}>
            <Button
              type="link"
              className="!px-0 !text-accent"
              onClick={() => startEdit(row)}
            >
              {t("common.edit")}
            </Button>
            <Popconfirm
              title={t("security.deleteConfirm")}
              okText={t("security.deleteOk")}
              cancelText={t("common.cancel")}
              onConfirm={() => confirmDelete(row)}
              placement="left"
              className={styles.deleteConfirm}
            >
              <Button type="link" danger className="!px-0">
                {t("common.delete")}
              </Button>
            </Popconfirm>
          </Flex>
        ),
    },
  ];

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-line bg-white p-5">
      <div>
        <Typography.Title level={5} className="!m-0">
          {t(copy.title)}
        </Typography.Title>
        <Typography.Paragraph type="secondary" className="!mt-2 !mb-0">
          {t(copy.desc)}
        </Typography.Paragraph>
      </div>

      <Flex gap={12} align="start">
        <div className="flex-1">
          <Input
            value={value}
            maxLength={512}
            status={error ? "error" : undefined}
            placeholder={t(copy.placeholder)}
            onChange={(e) => {
              setValue(e.target.value);
              setError("");
            }}
            onPressEnter={submitAdd}
          />
          {error ? (
            <Typography.Text type="danger" className="!mt-1 block">
              {error}
            </Typography.Text>
          ) : null}
        </div>
        <BrandButton disabled={atMax} loading={adding} onClick={submitAdd}>
          {t("security.add")}
        </BrandButton>
        <BrandButton variant="outline" onClick={() => setBatchOpen(true)}>
          {t("security.batchModify")}
        </BrandButton>
      </Flex>

      <Table
        rowKey={(row) => row.id || row.value}
        columns={columns}
        dataSource={list}
        pagination={false}
        locale={{ emptyText: t("common.empty") }}
      />

      <BatchModifyModal
        open={batchOpen}
        kind={kind}
        list={list.map((item) => item.value)}
        onCancel={() => setBatchOpen(false)}
        onSave={async (items) => {
          const next = await replaceSecurityRules(appId, {
            ruleType: RULE_TYPE[kind],
            values: items,
          });
          onListChange(() => next);
          setBatchOpen(false);
        }}
      />
    </section>
  );
}
